import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { ServicePackage, PackageFormData, PackagePriceModel, PricingTier } from '../../services/api'
import ConfirmModal from '../ui/ConfirmModal'

/**
 * PackageManager — CRUD UI for service_packages (SKU bundles).
 *
 * What a package is: the salesperson-facing bundle — "Shield" — that
 * expands at apply-time into N client_services rows (JumpCloud +
 * CrowdStrike). Same data model as regular services, just grouped.
 *
 * What this screen does:
 *   - List active (and optionally inactive) packages with their
 *     components inline.
 *   - Create / edit packages including the component list.
 *   - Soft-delete packages (they keep existing client_services
 *     references intact).
 *
 * What it doesn't do (on purpose, roadmap item for Sprint 8):
 *   - Pricing tiers by volume (1-10, 11-50, 51+).
 *   - Currency switching at the package level.
 *   - Margin simulation vs. vendor cost.
 */

interface ServiceOption {
  id: string
  name: string
  category: string
  base_price: number
  pricing_type: string
}

const PRICE_MODEL_LABEL: Record<PackagePriceModel, string> = {
  per_user: 'Por usuario/mes',
  fixed_monthly: 'Fijo mensual',
  custom: 'Custom',
}

const PRICE_MODEL_DESCRIPTION: Record<PackagePriceModel, string> = {
  per_user: 'Cantidad por componente × cantidad de usuarios al aplicar',
  fixed_monthly: 'Cantidad por componente tal cual, sin multiplicar',
  custom: 'Para pricing especial — sin auto-cálculo',
}

export default function PackageManager() {
  const [packages, setPackages] = useState<ServicePackage[]>([])
  const [services, setServices] = useState<ServiceOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editing, setEditing] = useState<ServicePackage | 'new' | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ServicePackage | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [showInactive, setShowInactive] = useState(false)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [pkgRes, svcRes] = await Promise.all([
        api.listPackages(),
        api.listServices(),
      ])
      if (!pkgRes.success) throw new Error(pkgRes.error || 'Error al cargar paquetes')
      setPackages((pkgRes.data as { packages: ServicePackage[] }).packages || [])
      if (svcRes.success) {
        const list = ((svcRes.data as any)?.services || []).map((s: any) => ({
          id: s.id, name: s.name, category: s.category,
          base_price: s.base_price, pricing_type: s.pricing_type,
        }))
        setServices(list)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      const res = await api.deletePackage(deleteTarget.id)
      if (!res.success) throw new Error(res.error || 'Error al eliminar')
      await load()
      setDeleteTarget(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setDeleteBusy(false)
    }
  }

  const visiblePackages = packages.filter(p => showInactive || p.isActive)

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-jarix-orange mx-auto mb-3" />
        <p className="text-gray-400">Cargando paquetes…</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Paquetes vendibles</h2>
          <p className="text-gray-400 text-sm mt-1">
            {packages.length} paquete(s) · {packages.filter(p => p.isActive).length} activos.
            Cada paquete se expande en varios servicios al asignarlo al cliente.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={e => setShowInactive(e.target.checked)}
              className="w-4 h-4 accent-jarix-orange"
            />
            Mostrar inactivos
          </label>
          <button
            onClick={() => setEditing('new')}
            className="bg-jarix-orange hover:bg-jarix-pink text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo paquete
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}

      {visiblePackages.length === 0 ? (
        <div className="bg-gray-800/50 border border-dashed border-gray-700 rounded-xl p-10 text-center">
          <p className="text-gray-400">No hay paquetes todavía. Creá el primero arriba.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {visiblePackages.map(pkg => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              onEdit={() => setEditing(pkg)}
              onDelete={() => setDeleteTarget(pkg)}
            />
          ))}
        </div>
      )}

      {editing && (
        <PackageFormModal
          pkg={editing === 'new' ? null : editing}
          services={services}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          open={true}
          title={`¿Enviar "${deleteTarget.name}" a la papelera?`}
          message="El paquete se ocultará de la lista pero los servicios ya asignados a clientes no se tocan."
          variant="danger"
          confirmLabel="Enviar a papelera"
          busy={deleteBusy}
          onConfirm={confirmDelete}
          onCancel={() => !deleteBusy && setDeleteTarget(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------
// Package card — compact summary + expandable components list
// ---------------------------------------------------------------
function PackageCard({ pkg, onEdit, onDelete }: {
  pkg: ServicePackage
  onEdit: () => void
  onDelete: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const totalBase = pkg.components.reduce((s, c) => {
    const unit = c.overridePrice ?? c.serviceBasePrice
    return s + unit * c.quantity * (1 - (c.discountPercent || 0) / 100)
  }, 0)
  const savings = pkg.priceModel === 'per_user' ? totalBase - pkg.displayPrice : null

  return (
    <div className={`rounded-xl border ${pkg.isActive ? 'border-gray-700 bg-gray-800' : 'border-gray-800 bg-gray-800/40'} overflow-hidden`}>
      <div className="flex items-center justify-between p-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className={`text-lg font-semibold ${pkg.isActive ? 'text-white' : 'text-gray-500'}`}>
              {pkg.name}
            </h3>
            <span className="text-xs font-mono text-gray-500">{pkg.slug}</span>
            {!pkg.isActive && (
              <span className="text-[10px] uppercase tracking-wider bg-gray-700 text-gray-400 px-2 py-0.5 rounded">
                Inactivo
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
              {PRICE_MODEL_LABEL[pkg.priceModel]}
            </span>
          </div>
          {pkg.description && (
            <p className="text-gray-400 text-sm">{pkg.description}</p>
          )}
          <div className="flex gap-4 mt-2 text-sm">
            <span>
              <span className="text-gray-500">Precio:</span>{' '}
              <span className="text-green-400 font-semibold">
                ${pkg.displayPrice} {pkg.currency}
                {pkg.priceModel === 'per_user' && <span className="text-gray-500 text-xs">/usuario/mes</span>}
                {pkg.priceModel === 'fixed_monthly' && <span className="text-gray-500 text-xs">/mes</span>}
              </span>
            </span>
            <span>
              <span className="text-gray-500">Componentes:</span>{' '}
              <span className="text-white">{pkg.components.length}</span>
            </span>
            {savings !== null && savings > 0 && (
              <span title="Descuento del bundle vs. suma de componentes" className="text-orange-300">
                -${savings.toFixed(0)} vs. suma
              </span>
            )}
            {pkg.pricingTiers && pkg.pricingTiers.length > 0 && (
              <span
                title={pkg.pricingTiers.map(t =>
                  `${t.min_users}-${t.max_users ?? '∞'} users → $${t.price_per_user}/user`
                ).join(' · ')}
                className="text-purple-300 text-xs px-2 py-0.5 bg-purple-500/10 border border-purple-500/30 rounded"
              >
                {pkg.pricingTiers.length} tiers
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-gray-400 hover:text-white p-2 rounded hover:bg-gray-700"
            title={expanded ? 'Ocultar componentes' : 'Ver componentes'}
          >
            <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={onEdit}
            className="text-gray-300 hover:text-jarix-orange p-2 rounded hover:bg-gray-700"
            title="Editar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="text-gray-300 hover:text-red-400 p-2 rounded hover:bg-gray-700"
            title="Eliminar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-700 bg-gray-900/50 p-4">
          {pkg.components.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              Sin componentes. Editá el paquete para agregarlos.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2">Servicio</th>
                  <th className="text-left py-2">Frecuencia</th>
                  <th className="text-right py-2">Cantidad</th>
                  <th className="text-right py-2">Precio</th>
                  <th className="text-right py-2">Dto.</th>
                  <th className="text-right py-2">Línea</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {pkg.components.map(c => {
                  const unit = c.overridePrice ?? c.serviceBasePrice
                  const lineTotal = unit * c.quantity * (1 - (c.discountPercent || 0) / 100)
                  return (
                    <tr key={c.id} className="text-gray-300">
                      <td className="py-2">
                        <div className="font-medium text-white">{c.serviceName}</div>
                        {c.serviceCategory && (
                          <div className="text-xs text-gray-500">{c.serviceCategory}</div>
                        )}
                      </td>
                      <td className="py-2 capitalize">{c.billingFrequency}</td>
                      <td className="py-2 text-right">{c.quantity}</td>
                      <td className="py-2 text-right">
                        ${unit.toFixed(2)}
                        {c.overridePrice !== null && c.overridePrice !== c.serviceBasePrice && (
                          <span className="text-orange-400 text-xs ml-1" title={`Catálogo: $${c.serviceBasePrice}`}>
                            (override)
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {c.discountPercent > 0 ? `-${c.discountPercent}%` : '—'}
                      </td>
                      <td className="py-2 text-right font-semibold text-green-400">
                        ${lineTotal.toFixed(2)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------
// Package form modal — create + edit, includes component editor
// ---------------------------------------------------------------
function PackageFormModal({ pkg, services, onClose, onSaved }: {
  pkg: ServicePackage | null
  services: ServiceOption[]
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const [formData, setFormData] = useState<PackageFormData>({
    name: pkg?.name || '',
    slug: pkg?.slug || '',
    description: pkg?.description || '',
    priceModel: pkg?.priceModel || 'per_user',
    displayPrice: pkg?.displayPrice || 0,
    currency: pkg?.currency || 'USD',
    isActive: pkg?.isActive ?? true,
    sortOrder: pkg?.sortOrder || 0,
    pricingTiers: pkg?.pricingTiers || [],
    components: pkg?.components.map(c => ({
      serviceId: c.serviceId,
      quantity: c.quantity,
      overridePrice: c.overridePrice,
      discountPercent: c.discountPercent,
      billingFrequency: c.billingFrequency,
      sortOrder: c.sortOrder,
    })) || [],
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Tier editor helpers
  const addTier = () => {
    setFormData(d => {
      const existing = d.pricingTiers || []
      // Default next tier picks up where the last one left off (+1).
      const last = existing[existing.length - 1]
      const nextMin = last && last.max_users ? last.max_users + 1 : 1
      return {
        ...d,
        pricingTiers: [...existing, {
          min_users: nextMin,
          max_users: null,
          price_per_user: d.displayPrice || 0,
        }],
      }
    })
  }
  const updateTier = (idx: number, patch: Partial<PricingTier>) => {
    setFormData(d => ({
      ...d,
      pricingTiers: (d.pricingTiers || []).map((t, i) => i === idx ? { ...t, ...patch } : t),
    }))
  }
  const removeTier = (idx: number) => {
    setFormData(d => ({
      ...d,
      pricingTiers: (d.pricingTiers || []).filter((_, i) => i !== idx),
    }))
  }

  const addComponent = () => {
    if (services.length === 0) return
    setFormData(d => ({
      ...d,
      components: [...(d.components || []), {
        serviceId: services[0].id,
        quantity: 1,
        overridePrice: null,
        discountPercent: 0,
        billingFrequency: 'monthly',
        sortOrder: (d.components || []).length,
      }],
    }))
  }

  const updateComponent = (idx: number, patch: Partial<NonNullable<PackageFormData['components']>[number]>) => {
    setFormData(d => ({
      ...d,
      components: (d.components || []).map((c, i) => i === idx ? { ...c, ...patch } : c),
    }))
  }

  const removeComponent = (idx: number) => {
    setFormData(d => ({
      ...d,
      components: (d.components || []).filter((_, i) => i !== idx),
    }))
  }

  const submit = async () => {
    setError(null)
    if (!formData.name.trim() || !formData.slug.trim()) {
      setError('Nombre y slug son requeridos')
      return
    }
    if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      setError('Slug inválido (solo minúsculas, números y guiones)')
      return
    }
    setSaving(true)
    try {
      const res = pkg
        ? await api.updatePackage(pkg.id, formData)
        : await api.createPackage(formData)
      if (!res.success) throw new Error(res.error || 'Error al guardar')
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setSaving(false)
    }
  }

  // Auto-generate slug from name when creating
  const handleName = (name: string) => {
    setFormData(d => {
      const slug = !pkg && !d.slug
        ? name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        : d.slug
      return { ...d, name, slug }
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-3xl max-h-[90vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {pkg ? `Editar: ${pkg.name}` : 'Nuevo paquete'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-300 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => handleName(e.target.value)}
                placeholder="Shield"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-jarix-orange"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Slug *</label>
              <input
                type="text"
                value={formData.slug}
                onChange={e => setFormData(d => ({ ...d, slug: e.target.value.toLowerCase() }))}
                placeholder="shield"
                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 font-mono focus:outline-none focus:border-jarix-orange"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Descripción</label>
            <textarea
              value={formData.description || ''}
              onChange={e => setFormData(d => ({ ...d, description: e.target.value }))}
              rows={2}
              placeholder="EDR + IAM — CrowdStrike Falcon + JumpCloud"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-jarix-orange"
            />
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Modelo de precio</label>
              <select
                value={formData.priceModel}
                onChange={e => setFormData(d => ({ ...d, priceModel: e.target.value as PackagePriceModel }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-jarix-orange"
              >
                {(Object.keys(PRICE_MODEL_LABEL) as PackagePriceModel[]).map(k => (
                  <option key={k} value={k}>{PRICE_MODEL_LABEL[k]}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {PRICE_MODEL_DESCRIPTION[formData.priceModel || 'per_user']}
              </p>
            </div>
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Precio anunciado</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.displayPrice || 0}
                onChange={e => setFormData(d => ({ ...d, displayPrice: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-jarix-orange"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1">Estado</label>
              <select
                value={formData.isActive === false ? 'inactive' : 'active'}
                onChange={e => setFormData(d => ({ ...d, isActive: e.target.value === 'active' }))}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded px-3 py-2 focus:outline-none focus:border-jarix-orange"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
          </div>

          {/* Pricing tiers editor (only meaningful for per_user packages).
              Empty = flat pricing. Sprint 7.1 feature. */}
          {formData.priceModel === 'per_user' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-white">Pricing tiers por volumen</h3>
                  <p className="text-xs text-gray-500">
                    Opcional. Define precios distintos según la cantidad de usuarios. Vacío = precio plano.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addTier}
                  className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Agregar tier
                </button>
              </div>

              {(formData.pricingTiers || []).length === 0 ? (
                <div className="border border-dashed border-gray-700 rounded p-3 text-xs text-gray-500 text-center">
                  Sin tiers — se usa <span className="text-white">${formData.displayPrice || 0}/user</span> para cualquier cantidad.
                </div>
              ) : (
                <div className="space-y-2">
                  {(formData.pricingTiers || []).map((t, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-900/60 border border-gray-700 rounded p-2">
                      <span className="col-span-1 text-xs text-gray-500 text-center">Tier {idx + 1}</span>
                      <div className="col-span-3">
                        <label className="block text-[10px] text-gray-500 uppercase">Desde</label>
                        <input
                          type="number" min="1" step="1"
                          value={t.min_users}
                          onChange={e => updateTier(idx, { min_users: parseInt(e.target.value) || 1 })}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm text-right"
                        />
                      </div>
                      <div className="col-span-3">
                        <label className="block text-[10px] text-gray-500 uppercase">Hasta (vacío = ∞)</label>
                        <input
                          type="number" min="1" step="1"
                          value={t.max_users ?? ''}
                          onChange={e => updateTier(idx, { max_users: e.target.value === '' ? null : parseInt(e.target.value) })}
                          placeholder="∞"
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm text-right"
                        />
                      </div>
                      <div className="col-span-4">
                        <label className="block text-[10px] text-gray-500 uppercase">$/usuario/mes</label>
                        <input
                          type="number" min="0" step="0.01"
                          value={t.price_per_user}
                          onChange={e => updateTier(idx, { price_per_user: parseFloat(e.target.value) || 0 })}
                          className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm text-right text-green-400 font-semibold"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTier(idx)}
                        className="col-span-1 text-gray-500 hover:text-red-400 text-sm mt-3"
                        title="Quitar tier"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <p className="text-[11px] text-gray-500 italic">
                    Los tiers se deben ordenar sin solaparse. El backend valida antes de guardar.
                    Cuando aplicás el paquete, el precio por usuario se ajusta según la cantidad de usuarios y
                    se distribuye proporcionalmente entre los componentes.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Components editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white">Componentes del paquete</h3>
              <button
                type="button"
                onClick={addComponent}
                disabled={services.length === 0}
                className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Agregar componente
              </button>
            </div>

            {(formData.components || []).length === 0 ? (
              <div className="border border-dashed border-gray-700 rounded p-4 text-center text-sm text-gray-500">
                Sin componentes. Agregá servicios del catálogo que se incluirán en este paquete.
              </div>
            ) : (
              <div className="space-y-2">
                {(formData.components || []).map((c, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-gray-900/60 border border-gray-700 rounded p-2">
                    <select
                      value={c.serviceId}
                      onChange={e => updateComponent(idx, { serviceId: e.target.value })}
                      className="col-span-4 bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm"
                    >
                      {services.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name} (${s.base_price})
                        </option>
                      ))}
                    </select>
                    <input
                      type="number" min="0.01" step="0.01"
                      value={c.quantity ?? 1}
                      onChange={e => updateComponent(idx, { quantity: parseFloat(e.target.value) || 1 })}
                      placeholder="Qty"
                      className="col-span-1 bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm text-right"
                      title="Cantidad"
                    />
                    <input
                      type="number" min="0" step="0.01"
                      value={c.overridePrice ?? ''}
                      onChange={e => updateComponent(idx, { overridePrice: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      placeholder="Precio override"
                      className="col-span-2 bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm text-right"
                      title="Dejar vacío para usar precio del catálogo"
                    />
                    <input
                      type="number" min="0" max="100" step="1"
                      value={c.discountPercent ?? 0}
                      onChange={e => updateComponent(idx, { discountPercent: parseFloat(e.target.value) || 0 })}
                      placeholder="Dto %"
                      className="col-span-1 bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm text-right"
                      title="Descuento %"
                    />
                    <select
                      value={c.billingFrequency || 'monthly'}
                      onChange={e => updateComponent(idx, { billingFrequency: e.target.value as any })}
                      className="col-span-3 bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm"
                    >
                      <option value="monthly">Mensual</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="annual">Anual</option>
                      <option value="one_time">Único</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeComponent(idx)}
                      className="col-span-1 text-gray-500 hover:text-red-400 text-sm"
                      title="Quitar componente"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving}
                  className="px-3 py-1.5 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving}
                  className="px-4 py-1.5 text-sm rounded bg-jarix-orange hover:bg-jarix-pink text-white font-semibold disabled:opacity-50 flex items-center gap-2">
            {saving && <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />}
            {pkg ? 'Guardar cambios' : 'Crear paquete'}
          </button>
        </div>
      </div>
    </div>
  )
}
